<template>
  <b-table
    ref="buyLeadsTable"
    class="b-table shadow table border mb-0"
    :tbody-class="'bg-white'"
    hover
    striped
    show-empty
    :empty-text="noLeadsText"
    :no-border-collapse="true"
    :items="leadsProvider"
    :fields="fields"
    :busy.sync="busy"
    :per-page="displayRows"
    :current-page="1"
    no-provider-paging
    :tbody-tr-attr="setRowAttributes"
    @row-clicked="goToLead"
  >
    <template v-slot:cell(date)="data">
      <div class="first-col">
        <b-badge
          v-if="!olderThanNow(data.item.priority_access)"
          class="priority-badge d-flex align-items-center rounded"
        >
          <span class="float-left" v-html="$t('Priority Access')" />
          <radial-progress-bar
            :class="'ml-1'"
            :stroke-width="2"
            :inner-stroke-width="2"
            :completed-steps="3"
            :diameter="20"
            :start-color="'#fff'"
            :stop-color="'#fff'"
            :inner-stroke-color="'#013652'"
            :total-steps="12"
          />
        </b-badge>
        <div :class="[{'priority-lead': !olderThanNow(data.item.priority_access)}, data.item.viewed === 0 ? 'font-weight-600' : 'font-italic']">
          {{ data.item.dateReleased | parseDate }}<br>{{ data.item.dateReleased | parseTime }}
        </div>
      </div>
    </template>
    <template v-slot:cell(postcode)="data">
      <div class="d-flex justify-content-start align-items-center">
        <span
          v-if="data.item.inCoverage===1"
          :class="['status-icon', 'mx-1', 'text-success']"
          title="Within Coverage Area"
        >
          <font-awesome-icon :icon="'circle'" />
        </span>
        <span v-else :class="['status-icon', 'mx-1', 'text-info']" title="Outside Coverage Area">
          <font-awesome-icon :icon="'circle'" />
        </span>
        <span :class="[data.item.viewed === 0 ? 'font-weight-600' : 'font-italic', 'ml-2']"
              v-html="leadShortAddressHtml(data.item.location, data.item[0].postcode, data.item.location_url)"
        />
      </div>
    </template>
    <template v-slot:cell(workType)="data">
      <span :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
        {{ data.item.work_type_translation }}
      </span>
    </template>
    <template v-slot:cell(timescale)="data">
      <span :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
        {{ getTimescaleTranslation(data.item[0].question_answers) }}
      </span>
    </template>
    <template v-slot:cell(sold)="data">
      <span :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
        {{ data.item[0].sold }}/{{ data.item[0].number_leads_requested }} {{ leadsSold(data.item[0].sold, data.item[0].number_leads_requested) }}
      </span>
    </template>
    <template v-slot:cell(qualified)="data">
      <span :class="['text-success', 'd-flex', 'justify-content-center']">
        <font-awesome-icon v-if="data.item.qualified === 1" title="Telephone Qualified" size="lg" icon="user-check" />
        <font-awesome-icon v-else title="Telephone Verified" size="lg" :icon="['far', 'check-circle']" />
      </span>
    </template>
    <template v-slot:cell(dashboardPrice)="data">
      <span :class="data.item.viewed === 0 ? 'font-weight-600' : 'font-italic'">
        {{ $n(data.item.dashboardPrice, 'currency') }}
      </span>
    </template>
    <template v-slot:cell(buy)="data">
      <div class="d-flex">
        <router-link
          class="btn-sm btn m-auto btn-outline-primary"
          :to="{name:'buy-leads', query:{enquirycode: data.item[0].quote_code}}"
        >
          {{ $t('View') }}
        </router-link>
      </div>
    </template>
    <template v-slot:custom-foot>
      <b-tr ref="tableFooter" class="bg-white">
        <b-td colspan="8" class="text-center p-3">
          <router-link class="btn btn-sm btn-outline-secondary mr-2" :to="{name:'my-leads'}">
            {{ $t('My Leads') }}
          </router-link>
          <router-link class="btn btn-sm btn-outline-secondary" :to="{name:'buy-leads'}">
            {{ $t('Buy Leads') }}
          </router-link>
        </b-td>
      </b-tr>
    </template>
    <template v-slot:table-busy>
      <div class="text-center bg-white text-dark my-2">
        <spinner :displayLoading="true" />
      </div>
    </template>
  </b-table>
</template>

<script>
import LeadsSoldMixin from "@/imports/mixins/leadsSold";
import LeadShortAddressMixin from "@/imports/mixins/leadShortAddress";
import Spinner from "@/components/animation/Spinner";
import OlderThanNow from "@/imports/mixins/olderThanNow";
import RadialProgressBar from 'vue-radial-progress';
import company from "@/imports/mixins/company";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";
import { captureError } from "@/services/userMonitoring";

export default {
  name: "MarketplaceLeadsTableLite",
  components: {Spinner, RadialProgressBar},
  mixins:[LeadsSoldMixin,LeadShortAddressMixin, OlderThanNow, company, workTypeTranslation],
  props: ["displayRows"],
  data: function() {
    return {
      fields: [
        {key:'date', label:'Date & Time', canSort:true, class: 'first-col'},
        {key:'postcode', label:'Postcode', formatter:'postcode', canSort:true},
        {key:'workType', label:'Lead Type', formatter:'workType', canSort:false},
        {key:'timescale', label:'Timescale', formatter:'timescale', canSort:true},
        {key:'sold', label:'Leads Sold', canSort:true},
        {key:'dashboardPrice', label:'Price', canSort:true},
        {key:'buy', label:''},
      ],
      filter: {
        'coverage': 1,
        'page': 1,
        'pageSize': 10,
        'auto-switch': true
      },
      busy: false,
      rowHeight: 60,
    }
  },
  computed: {
    footerHeight() {
      return this.$refs.tableFooter.$el.offsetHeight;
    },
    isBanned: function() {
      return (this.company) ? this.company.no_marketplace : false;
    },
    noLeadsText: function (){
      return (this.isBanned)
        ? this.$t('There are currently no leads available. Please keep an eye out for any Priority Lead Alerts')
        : this.$t('No leads matching your search criteria were found. Please adjust the filters and try again')
    },
  },
  methods: {
    leadsProvider: async function () {
      try {
        let result = await this.$api.leads.marketplace(this.filter);
        result.data.data.forEach((lead) => lead[0].sold = Math.max(lead[0].sold, 0));
        return result.data.data;
      } catch (e) {
        captureError(e, {
          section: "marketplace",
          action: "get-leads",
          type: "api-request"
        });
      }
    },
    goToLead: function(record) {
      this.$router.push({ path: 'buy-leads', query: { enquirycode: record[0].quote_code } })
    },
    setRowAttributes: function(item, type) {
      if (!item || type !== 'row') return;
      return {
        title: 'Qualification notes: ' + (item[0].description !== null && item[0].description.length > 0 ? item[0].description : 'none provided'),
        style: "cursor: pointer;",
      }
    },
  }
}
</script>

<style scoped lang="scss">
@media only screen and (min-width: 1200px) and (max-width: 1500px) {
  .table {
    font-size: .7rem;

    .btn-sm {
      font-size: .7rem;
      min-width: 70px;
    }
  }
}

@media only screen and (min-width: 1500px) {
  .table {
    .first-col {
      padding-left: 1rem;
    }
  }
}

.first-col {
  position: relative;
  padding-left: 1rem;

  .priority-badge {
    font-size: 10px;
    background: linear-gradient(to right, $notification-icon-color, #dbfbfc);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
    color: $theme-primary;
    max-width: 70px;
    margin-bottom: 5px;
    transform: scale(.8);
    position: absolute;

    @media only screen and (max-width: 1200px) {
      top: -5px;
    }

    @media only screen and (min-width: 1201px) and (max-width: 1399px) {
      left: -40px;
      top: 7px;
    }

    @media only screen and (max-width: 1399px) {
      transform: scale(.8);
    }

    @media only screen and (min-width: 1400px) {
      transform: scale(.9);
      left: -50px;
      top: 7px;
    }
  }

  .priority-lead {
    @media only screen and (max-width: 1200px) {
      padding-top: 25px;
    }
    @media only screen and (min-width: 1200px) {
      padding-left: 10px;
    }
  }
}
</style>
